/*
Geolocation helper. Mirrors the Android "fresh fix" guard (< 60 s old,
< 30 m horizontal accuracy) so capture validation stays consistent
across platforms.

Two-tier resolution:
1. **Cached fix** pushed by the MapLibre GeolocateControl via `recordFix`
   each time it gets a callback. This is what the user sees as the blue
   dot — using it for captures means the geolocation is already warm and
   the capture is instant.
2. **Native API fallback** with a hardened outer timeout. iOS Safari in
   standalone PWA mode has a long-standing bug where
   `getCurrentPosition` can hang forever even with a `timeout` option;
   we layer our own setTimeout that rejects the Promise so the capture
   flow's status callback always fires.

iOS Safari quirks:
- Geolocation requires a user gesture on first use to surface the prompt
  reliably. Call this from inside a click handler.
- Background updates do NOT work in standalone PWA mode. We only ever
  try to get a foreground fix.
*/

const kMaxAgeMs: number = 60_000  // a fix older than 60 s is rejected
const kMinAccuracyMeters: number = 30  // a fix coarser than 30 m is rejected
const kTimeoutMs: number = 20_000  // outer JS-level timeout — independent of the OS timeout option
const kEarthRadiusMeters: number = 6_371_000  // for haversine distance computations

export interface FreshFix
{
	lat: number
	lon: number
	accuracyMeters: number
	timestamp: number
}

let fLatestFix: FreshFix | null = null  // most recent fix from any source, used to short-circuit capture flow
const fListeners = new Set<(inFix: FreshFix | null) => void>()  // observers for live distance widgets etc.

/*
Record a fix received from any source (typically the MapLibre
GeolocateControl). Capture flow prefers this over a fresh native call
because the control's watchPosition is already running. Notifies
subscribers synchronously so the UI can refresh distance pills.
*/
export function recordFix(inFix: FreshFix): void
{
	fLatestFix = inFix
	for (const vFn of fListeners) vFn(inFix)
}

/*
Read the most recent fix without triggering a fresh OS call. Returns
null when no fix has ever been recorded this session OR when the cached
one is too stale / coarse to be usable.
*/
export function getLatestFix(): FreshFix | null
{
	if (!fLatestFix) return null
	if (Date.now() - fLatestFix.timestamp > kMaxAgeMs) return null
	if (fLatestFix.accuracyMeters > kMinAccuracyMeters) return null
	return fLatestFix
}

/*
Subscribe to fix updates. Callback fires once immediately with the
current fix (may be null) and again on every recordFix() call. Returns
an unsubscribe closure — call it on screen unmount / sheet close.
*/
export function subscribeFix(inFn: (inFix: FreshFix | null) => void): () => void
{
	fListeners.add(inFn)
	inFn(getLatestFix())
	return () => { fListeners.delete(inFn) }
}

/*
Request a fresh GPS fix. Tries the cached fix first; falls back to
navigator.geolocation with a JS-level outer timeout so iOS hangs cannot
deadlock the capture flow.
*/
export async function requestFreshFix(): Promise<FreshFix>
{
	const vCached = pickCached()  // fresh-enough cached fix, null otherwise
	if (vCached) return vCached

	if (!('geolocation' in navigator))
	{
		throw new Error('Geolocation API not available')
	}

	return new Promise<FreshFix>((inResolve, inReject) => {
		let vSettled = false  // single-fire guard for the resolver/rejector pair

		const vSettle = (inFn: () => void): void => {
			if (vSettled) return
			vSettled = true
			inFn()
		}

		// Outer JS-level timeout. Independent of the geolocation `timeout`
		// option because iOS Safari standalone PWAs have been observed to
		// silently never call back. This guarantees the Promise resolves.
		const vOuterTimer = window.setTimeout(() => {
			vSettle(() => inReject(new Error('Fix too stale: timeout')))
		}, kTimeoutMs)

		navigator.geolocation.getCurrentPosition(
			(inPos) => {
				window.clearTimeout(vOuterTimer)
				const vAccuracy: number = inPos.coords.accuracy  // meters
				const vAgeMs: number = Date.now() - inPos.timestamp  // staleness
				if (vAccuracy > kMinAccuracyMeters)
				{
					vSettle(() => inReject(new Error(`Fix too coarse: ${vAccuracy.toFixed(0)} m`)))
					return
				}
				if (vAgeMs > kMaxAgeMs)
				{
					vSettle(() => inReject(new Error(`Fix too stale: ${(vAgeMs / 1000).toFixed(0)} s`)))
					return
				}
				const vFix: FreshFix = {
					lat: inPos.coords.latitude,
					lon: inPos.coords.longitude,
					accuracyMeters: vAccuracy,
					timestamp: inPos.timestamp,
				}
				recordFix(vFix)  // cache for next call
				vSettle(() => inResolve(vFix))
			},
			(inErr) => {
				window.clearTimeout(vOuterTimer)
				vSettle(() => inReject(new Error(inErr.message || 'Geolocation failed')))
			},
			{ enableHighAccuracy: true, maximumAge: 0, timeout: kTimeoutMs - 2000 },
		)
	})
}

/*
Return the cached fix if it satisfies freshness + accuracy. Otherwise
null — caller should fall back to the native API.
*/
function pickCached(): FreshFix | null
{
	if (!fLatestFix) return null
	if (Date.now() - fLatestFix.timestamp > kMaxAgeMs) return null
	if (fLatestFix.accuracyMeters > kMinAccuracyMeters) return null
	return fLatestFix
}

/*
Great-circle distance between two WGS84 points, in meters. Standard
haversine — accurate enough for the < 100 m proximity checks used by
the capture flow + sheet distance widget.
*/
export function haversineMeters(inLat1: number, inLon1: number, inLat2: number, inLon2: number): number
{
	const vDLat = toRadians(inLat2 - inLat1)  // delta latitude in radians
	const vDLon = toRadians(inLon2 - inLon1)  // delta longitude in radians
	const vSlat = Math.sin(vDLat / 2)
	const vSlon = Math.sin(vDLon / 2)
	const vA = vSlat * vSlat + Math.cos(toRadians(inLat1)) * Math.cos(toRadians(inLat2)) * vSlon * vSlon
	return 2 * kEarthRadiusMeters * Math.asin(Math.min(1, Math.sqrt(vA)))
}

function toRadians(inDeg: number): number { return inDeg * Math.PI / 180 }
